import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";

/**
 * API Documentation Page
 * Interactive Swagger UI for Centre3 tRPC API
 * Accessible at: /api-docs
 */

export default function ApiDocs() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: openApiSpec, isLoading } = trpc.apiDocs.getSpec.useQuery();
  const { data: stats } = trpc.apiDocs.getStats.useQuery();
  const { data: routers } = trpc.apiDocs.getRouters.useQuery();

  useEffect(() => {
    if (!openApiSpec || !containerRef.current) return;

    // Load Swagger UI dynamically
    const loadSwaggerUI = async () => {
      const SwaggerUIBundle = (window as any).SwaggerUIBundle;
      const SwaggerUIStandalonePreset = (window as any).SwaggerUIStandalonePreset;

      if (!SwaggerUIBundle || !SwaggerUIStandalonePreset) {
        // Load Swagger UI scripts
        const script1 = document.createElement("script");
        script1.src = "https://cdn.jsdelivr.net/npm/swagger-ui-dist@3/swagger-ui-bundle.js";
        script1.async = true;

        const script2 = document.createElement("script");
        script2.src = "https://cdn.jsdelivr.net/npm/swagger-ui-dist@3/swagger-ui-standalone-preset.js";
        script2.async = true;

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://cdn.jsdelivr.net/npm/swagger-ui-dist@3/swagger-ui.css";

        document.head.appendChild(link);
        document.head.appendChild(script1);
        document.head.appendChild(script2);

        script2.onload = () => {
          initSwaggerUI();
        };
      } else {
        initSwaggerUI();
      }
    };

    const initSwaggerUI = () => {
      const SwaggerUIBundle = (window as any).SwaggerUIBundle;
      const SwaggerUIStandalonePreset = (window as any).SwaggerUIStandalonePreset;

      SwaggerUIBundle({
        spec: openApiSpec,
        dom_id: "#swagger-ui",
        deepLinking: true,
        presets: [
          SwaggerUIBundle.presets.apis,
          SwaggerUIStandalonePreset
        ],
        plugins: [
          SwaggerUIBundle.plugins.DownloadUrl
        ],
        layout: "StandaloneLayout",
        defaultModelsExpandDepth: 1,
        defaultModelExpandDepth: 1,
        tryItOutEnabled: true,
        requestInterceptor: (request: any) => {
          // Add session cookie to requests
          request.headers["Cookie"] = document.cookie;
          return request;
        },
        responseInterceptor: (response: any) => {
          return response;
        }
      });
    };

    loadSwaggerUI();
  }, [openApiSpec]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading API Documentation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Centre3 API Documentation</h1>
              <p className="text-sm text-gray-600 mt-1">Interactive API explorer with live testing</p>
            </div>
            {stats && (
              <div className="hidden sm:flex gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats.totalProcedures}</div>
                  <div className="text-xs text-gray-600">Endpoints</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats.totalRouters}</div>
                  <div className="text-xs text-gray-600">Routers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats.protectedEndpoints}</div>
                  <div className="text-xs text-gray-600">Protected</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {routers && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Available Routers</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {routers.map((router: any) => (
                <div
                  key={router.name}
                  className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <div className="font-medium text-sm text-gray-900 capitalize">{router.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{router.procedures} endpoints</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Swagger UI Container */}
      <div className="max-w-7xl mx-auto">
        <div
          id="swagger-ui"
          ref={containerRef}
          className="swagger-ui-container"
        />
      </div>

      {/* Custom Styles */}
      <style>{`
        .swagger-ui-container {
          padding: 20px;
        }

        .swagger-ui .topbar {
          display: none;
        }

        .swagger-ui .scheme-container {
          background: #fafafa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .swagger-ui .btn {
          border-radius: 6px;
          font-weight: 500;
        }

        .swagger-ui .btn-primary {
          background-color: #6366f1;
          border-color: #6366f1;
        }

        .swagger-ui .btn-primary:hover {
          background-color: #4f46e5;
          border-color: #4f46e5;
        }

        .swagger-ui .opblock {
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .swagger-ui .opblock.opblock-get {
          border-color: #16a34a;
          background-color: rgba(22, 163, 74, 0.05);
        }

        .swagger-ui .opblock.opblock-post {
          border-color: #2563eb;
          background-color: rgba(37, 99, 235, 0.05);
        }

        .swagger-ui .opblock.opblock-put {
          border-color: #ea580c;
          background-color: rgba(234, 88, 12, 0.05);
        }

        .swagger-ui .opblock.opblock-delete {
          border-color: #dc2626;
          background-color: rgba(220, 38, 38, 0.05);
        }

        .swagger-ui .opblock-summary {
          border-radius: 8px;
        }

        .swagger-ui .opblock-summary:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }

        .swagger-ui textarea {
          border-radius: 6px;
          border-color: #e5e7eb;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .swagger-ui input[type="text"],
        .swagger-ui input[type="password"],
        .swagger-ui input[type="email"] {
          border-radius: 6px;
          border-color: #e5e7eb;
        }

        .swagger-ui .model-container {
          background-color: #f9fafb;
          border-radius: 8px;
        }

        .swagger-ui .response-col_status {
          font-weight: 600;
          color: #1f2937;
        }

        .swagger-ui .response-col_description {
          color: #6b7280;
        }

        .swagger-ui .info .title {
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
        }

        .swagger-ui .info .description {
          color: #6b7280;
          font-size: 14px;
        }

        .swagger-ui .scheme-container .schemes > label {
          margin-right: 15px;
        }

        .swagger-ui .scheme-container .schemes > label > span {
          padding: 5px 10px;
          border-radius: 4px;
          background-color: #e5e7eb;
          color: #1f2937;
          font-weight: 500;
        }

        .swagger-ui .scheme-container .schemes > label input[type="radio"]:checked + span {
          background-color: #6366f1;
          color: white;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .swagger-ui-container {
            padding: 10px;
          }

          .swagger-ui .opblock {
            margin-bottom: 8px;
          }

          .swagger-ui .opblock-summary {
            padding: 10px;
          }

          .swagger-ui .opblock-summary-method {
            min-width: 60px;
          }

          .swagger-ui .opblock-summary-path {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}
